import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Sparkles, Zap, FileDown, Lightbulb, ChevronRight, Target, Circle, Ruler, Crosshair, Shield, Award } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { useSeasonStats } from "../lib/seasonStats";
import { ShotProfileGraphic, ProfileBars, TransitionCompare } from "./ShotProfileGraphic";
import { ShotProfileCard, profileBars, ProfileRecs } from "./ShotProfileCards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

const CATEGORY_ICONS = {
  rim:                 Target,     // attacking the rim
  mid_paint:           Circle,     // in the paint
  mid_range:           Ruler,      // measuring mid-range distance
  three_off_dribble:   Zap,        // off-the-dribble = movement/energy
  three_cs_guarded:    Shield,     // contested
  three_cs_unguarded:  Sparkles,   // open look / uncontested
  ft_rate:             Award,      // free-throw line
};

const CATS = [
  { key: "rim",                label: "Rim Attempts",              is3: false, primary: true },
  { key: "mid_paint",          label: "Mid Paint (Runners/Hooks)", is3: false, primary: true },
  { key: "mid_range",          label: "Mid Range",                 is3: false, primary: true },
  { key: "three_off_dribble",  label: "3PT Off Dribble",           is3: true,  primary: true },
  { key: "three_cs_guarded",   label: "3PT C+S Contested",   is3: true, primary: true },
  { key: "three_cs_unguarded", label: "3PT C+S Uncontested", is3: true, primary: true },
];

const emptyInputs = () => ({
  total_fga: "", fta: "", ftm: "",
  ...Object.fromEntries(CATS.map(c => [c.key, { fgm: "", fga: "" }])),
});

const n = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);
const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const num = (v, d = 2) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));

const RATING_STYLE = {
  great:        { label: "GREAT",         bg: "#065f46", text: "#d1fae5" },
  solid:        { label: "SOLID",         bg: "#3b3f22", text: "#d8b854" },
  needs_work:   { label: "NEEDS WORK",    bg: "#7f1d1d", text: "#fecaca" },
  insufficient: { label: "TOO SMALL",     bg: "#2a2a2a", text: "#9ca3af" },
};

export default function PlayerShotDiet({ playerId, playerName, seasonAverages }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [analyses, setAnalyses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [inputs, setInputs] = useState(emptyInputs());
  const [label, setLabel] = useState("");
  const [exporting, setExporting] = useState(false);
  const printRef = useRef({});
  const [pdfProfiles, setPdfProfiles] = useState({ transition: null, play_type: null });
  const onProfileSelected = (kind, doc) => setPdfProfiles(p => (p[kind] === doc ? p : { ...p, [kind]: doc }));
  const { records: seasonRecords } = useSeasonStats(playerId);
  const [ssSeason, setSsSeason] = useState("");
  useEffect(() => { if (!ssSeason && seasonRecords.length) setSsSeason(seasonRecords[0].season); }, [seasonRecords, ssSeason]);

  // Season Stats -> Shot Diet inputs. Floaters + hooks feed Mid Paint; corner 3s are part of C&S (already counted in SS-046..049).
  const pullFromSeasonStats = () => {
    const rec = seasonRecords.find(r => r.season === ssSeason);
    const s = rec?.stats || {};
    const v = (id) => (s[id] == null ? "" : String(s[id]));
    const sum = (...ids) => (ids.every(id => s[id] == null) ? "" : String(ids.reduce((t, id) => t + (Number(s[id]) || 0), 0)));
    const seed = {
      total_fga: v("SS-004"), fta: v("SS-008"), ftm: v("SS-009"),
      rim: { fga: v("SS-040"), fgm: v("SS-041") },
      mid_paint: { fga: sum("SS-042", "SS-044"), fgm: sum("SS-043", "SS-045") },
      mid_range: { fga: v("SS-052"), fgm: v("SS-053") },
      three_off_dribble: { fga: v("SS-050"), fgm: v("SS-051") },
      three_cs_guarded: { fga: v("SS-048"), fgm: v("SS-049") },
      three_cs_unguarded: { fga: v("SS-046"), fgm: v("SS-047") },
    };
    const filled = Object.values(seed).filter(x => (typeof x === "string" ? x !== "" : x.fga !== "" || x.fgm !== "")).length;
    if (!filled) { toast.error(`No usable numbers in Season Stats ${ssSeason}`, { description: "Needs FGA / FT / Synergy shot types" }); return; }
    setInputs(seed);
    if (!label) setLabel(`${ssSeason} · Season Stats`);
    toast.success(`Pulled ${filled} groups from Season Stats ${ssSeason}`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get(`/players/${playerId}/shot-diet`);
      setAnalyses(r.data.analyses || []);
      if ((r.data.analyses || []).length > 0) setSelected(r.data.analyses[0]);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [playerId]);

  const autofill = () => {
    const sa = seasonAverages;
    if (!sa) { toast.error("No season averages available"); return; }
    const gp = sa.games_played || 1;
    const totalFga = (sa.fga || 0) * gp;
    const threeFga = (sa.fg3a || 0) * gp;
    const threeFgm = (sa.fg3m || 0) * gp;
    const fta = (sa.fta || 0) * gp;
    // 2PT total = FGA − 3PA. Split into rim/paint/mid guessed: 55/15/30 of 2PA
    const twoFga = Math.max(0, totalFga - threeFga);
    const twoFgm = Math.max(0, (sa.fgm || 0) * gp - threeFgm);
    const rimPct = 0.55, paintPct = 0.15, midPct = 0.30;
    const seed = {
      total_fga: Math.round(totalFga),
      fta: Math.round(fta),
      ftm: Math.round((sa.ftm || 0) * gp),
      rim:       { fga: Math.round(twoFga * rimPct),   fgm: Math.round(twoFgm * rimPct) },
      mid_paint: { fga: Math.round(twoFga * paintPct), fgm: Math.round(twoFgm * paintPct) },
      mid_range: { fga: Math.round(twoFga * midPct),   fgm: Math.round(twoFgm * midPct) },
      three_off_dribble:   { fga: Math.round(threeFga * 0.30), fgm: Math.round(threeFgm * 0.30) },
      three_cs_guarded:    { fga: Math.round(threeFga * 0.35), fgm: Math.round(threeFgm * 0.35) },
      three_cs_unguarded:  { fga: Math.round(threeFga * 0.35), fgm: Math.round(threeFgm * 0.35) },
    };
    setInputs(seed);
    toast.success("Prefilled — adjust splits before running");
  };

  const submit = async () => {
    setCreating(true);
    try {
      const payload = {
        label: label.trim() || null,
        inputs: {
          total_fga: n(inputs.total_fga), fta: n(inputs.fta), ftm: n(inputs.ftm),
          ...Object.fromEntries(CATS.map(c => [c.key, { fgm: n(inputs[c.key]?.fgm), fga: n(inputs[c.key]?.fga) }])),
        },
      };
      const r = await client.post(`/players/${playerId}/shot-diet`, payload);
      toast.success("Analysis saved");
      setAnalyses(a => [r.data, ...a]);
      setSelected(r.data);
      setShowForm(false);
      setInputs(emptyInputs()); setLabel("");
    } catch (e) {
      toast.error("Analysis failed", { description: e?.response?.data?.detail || e.message });
    } finally { setCreating(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.label || 'Analysis'}"?`)) return;
    try {
      await client.delete(`/players/${playerId}/shot-diet/${a.id}`);
      setAnalyses(list => list.filter(x => x.id !== a.id));
      if (selected?.id === a.id) setSelected(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const updateAction = async (categoryKey, value) => {
    if (!selected) return;
    const nextActions = { ...(selected.coach_actions || {}), [categoryKey]: value };
    // Optimistic update
    setSelected(s => ({ ...s, coach_actions: nextActions }));
    setAnalyses(list => list.map(a => a.id === selected.id ? { ...a, coach_actions: nextActions } : a));
    try {
      await client.patch(`/players/${playerId}/shot-diet/${selected.id}`, { coach_actions: nextActions });
    } catch (e) {
      toast.error("Action save failed");
      load();  // rollback via reload
    }
  };

  const toggleIncrease = async (categoryKey, field) => {
    if (!selected) return;
    const prev = selected.increase_flags || {};
    const cur = prev[categoryKey] || { use: false, pps: false };
    const nextFlags = { ...prev, [categoryKey]: { ...cur, [field]: !cur[field] } };
    setSelected(s => ({ ...s, increase_flags: nextFlags }));
    setAnalyses(list => list.map(a => a.id === selected.id ? { ...a, increase_flags: nextFlags } : a));
    try {
      await client.patch(`/players/${playerId}/shot-diet/${selected.id}`, { increase_flags: nextFlags });
    } catch (e) {
      toast.error("Save failed");
      load();
    }
  };

  const exportPDF = async () => {
    const pages = [printRef.current?.page1, printRef.current?.page2].filter(Boolean);
    if (pages.length === 0) return;
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 30;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage("letter", "landscape");
        const canvas = await html2canvas(pages[i], { backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false });
        const img = canvas.toDataURL("image/png");
        const scale = Math.min(maxW / canvas.width, maxH / canvas.height);
        const finalW = canvas.width * scale;
        const finalH = canvas.height * scale;
        pdf.addImage(img, "PNG", (pageW - finalW) / 2, margin, finalW, finalH);
      }
      const safe = (playerName || "Player").replace(/\s+/g, "-");
      pdf.save(`${safe}-Shot-Diet-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); }
    finally { setExporting(false); }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="player-shot-diet">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-[var(--org-accent)]" />
          <div>
            <h2 className="font-heading uppercase text-2xl tracking-[0.2em] text-white">Shot Diet</h2>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#a0a0a0] mt-0.5">AI-powered shot selection analysis · Claude Sonnet 5</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !showForm && (
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)]"
              data-testid="sd-new-btn"><Plus className="w-3 h-3" /> New Analysis</button>
          )}
          {selected && (
            <button onClick={exportPDF} disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-60"
              data-testid="sd-pdf-btn"><FileDown className="w-3 h-3" /> {exporting ? "Exporting…" : "Share as PDF"}</button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="border border-[var(--org-accent)]/50 bg-[#141414] p-6 mb-6" data-testid="sd-form">
          <div className="flex items-center gap-3 mb-4">
            <input value={label} onChange={e => setLabel(e.target.value)}
              placeholder="Label (e.g., Pre-Season Baseline)"
              className="flex-1 bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
              data-testid="sd-label-input" />
            {seasonRecords.length > 0 && (
              <>
                <select value={ssSeason} onChange={e => setSsSeason(e.target.value)} className="bg-[#0f0f0f] border border-white/10 px-2 py-2 text-[11px] font-mono text-white focus:outline-none focus:border-[var(--org-accent)]" data-testid="sd-ss-season">
                  {seasonRecords.map(r => <option key={r.season} value={r.season}>{r.season}</option>)}
                </select>
                <button onClick={pullFromSeasonStats}
                  className="flex items-center gap-2 px-3 py-2 border border-[var(--org-accent)] text-[var(--org-accent)] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent)]/10"
                  data-testid="sd-pull-season-stats-btn"><Zap className="w-3 h-3" /> Pull from Season Stats</button>
              </>
            )}
            <button onClick={autofill}
              className="flex items-center gap-2 px-3 py-2 border border-white/20 text-white text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
              data-testid="sd-autofill-btn"><Zap className="w-3 h-3" /> Autofill from Season Avg</button>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <IntField label="Total FGA (all zones)" value={inputs.total_fga} onChange={v => setInputs(i => ({ ...i, total_fga: v }))} tid="total_fga" />
            <IntField label="Free Throws Made (FTM)" value={inputs.ftm} onChange={v => setInputs(i => ({ ...i, ftm: v }))} tid="ftm" />
            <IntField label="Free Throw Attempts (FTA)" value={inputs.fta} onChange={v => setInputs(i => ({ ...i, fta: v }))} tid="fta" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            {CATS.map(c => (
              <div key={c.key} className="grid grid-cols-6 gap-3 items-center border-t border-white/5 pt-2">
                <div className="col-span-3 text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0]">{c.label}</div>
                <div className="col-span-1 flex items-center gap-2">
                  <span className="text-[9px] text-[#757575]">FGM</span>
                  <input type="number" value={inputs[c.key]?.fgm ?? ""} onChange={e => setInputs(i => ({ ...i, [c.key]: { ...i[c.key], fgm: e.target.value } }))}
                    className="w-full bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-white text-center focus:outline-none focus:border-[var(--org-accent)]"
                    data-testid={`sd-${c.key}-fgm`} />
                </div>
                <div className="col-span-1 flex items-center gap-2">
                  <span className="text-[9px] text-[#757575]">FGA</span>
                  <input type="number" value={inputs[c.key]?.fga ?? ""} onChange={e => setInputs(i => ({ ...i, [c.key]: { ...i[c.key], fga: e.target.value } }))}
                    className="w-full bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-white text-center focus:outline-none focus:border-[var(--org-accent)]"
                    data-testid={`sd-${c.key}-fga`} />
                </div>
                <div className="col-span-1 text-[11px] text-[#a0a0a0] text-right">
                  {n(inputs[c.key]?.fga) >= 1 ? `${((n(inputs[c.key]?.fgm) / n(inputs[c.key]?.fga)) * 100).toFixed(1)}%` : ""}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 mt-5">
            <button onClick={() => { setShowForm(false); setInputs(emptyInputs()); setLabel(""); }}
              className="px-4 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white"
              data-testid="sd-cancel">Cancel</button>
            <button onClick={submit} disabled={creating}
              className="flex items-center gap-2 px-5 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              data-testid="sd-submit">{creating ? <><Loader2 className="w-3 h-3 animate-spin" /> Analyzing…</> : <>Run Analysis</>}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* History sidebar */}
        <aside className="lg:col-span-1 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#757575] font-bold mb-2">History</div>
          {loading && <div className="text-[#757575] text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> loading…</div>}
          {!loading && analyses.length === 0 && <div className="text-[#757575] text-xs">No analyses yet.</div>}
          {analyses.map(a => (
            <div key={a.id} className={`border ${selected?.id === a.id ? "border-[var(--org-accent)]" : "border-white/10"} bg-[#141414] px-3 py-2 cursor-pointer group`}
                 onClick={() => setSelected(a)} data-testid={`sd-history-${a.id}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-xs text-white truncate">{a.label || "Analysis"}</div>
                  <div className="text-[9px] text-[#757575] font-mono mt-0.5">{(a.created_at || "").slice(0, 10)}</div>
                </div>
                {canEdit && <button onClick={(e) => { e.stopPropagation(); remove(a); }} className="opacity-0 group-hover:opacity-100 text-[#757575] hover:text-red-300"><Trash2 className="w-3 h-3" /></button>}
              </div>
            </div>
          ))}
        </aside>

        {/* Report */}
        <div className="lg:col-span-3">
          {selected ? <Report analysis={selected} playerName={playerName} printRef={printRef} canEdit={canEdit} onUpdateAction={updateAction} onToggleIncrease={toggleIncrease} profiles={pdfProfiles} /> :
            <div className="border border-dashed border-white/10 py-16 text-center text-[#757575] text-sm">
              {canEdit ? "Run a new analysis to see the coach report." : "No analysis available yet."}
            </div>}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-8">
        <ShotProfileCard playerId={playerId} kind="transition" title="Transition vs Half Court" description="Manual entry · Use %, PPP, 2FG%, 3FG%, FT Rate per situation." onSelectedChange={(d) => onProfileSelected("transition", d)} />
        <ShotProfileCard playerId={playerId} kind="play_type" title="Play Types" description="Manual entry · top 4–6 play types with Use % and PPP." onSelectedChange={(d) => onProfileSelected("play_type", d)} />
      </div>
    </section>
  );
}

function IntField({ label, value, onChange, tid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] font-bold mb-2">{label}</div>
      <input type="number" value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-lg font-heading text-white text-center focus:outline-none focus:border-[var(--org-accent)]"
        data-testid={`sd-${tid}`} />
    </div>
  );
}

function BoxSlot({ label, made, att, pct, pps }) {
  const mk = Math.round(made || 0);
  const at = Math.round(att || 0);
  const pctText = pct === null || pct === undefined ? "—" : `${(pct * 100).toFixed(1)}%`;
  const ppsText = pps === null || pps === undefined ? "—" : Number(pps).toFixed(2);
  return (
    <div className="border border-white/5 bg-[#101010] p-4">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] font-bold">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-heading text-2xl text-white">{mk}<span className="text-[#757575] mx-1">/</span>{at}</div>
        <div className="text-[13px] text-[var(--org-accent)] font-bold">{pctText}</div>
      </div>
      <div className="mt-2 pt-2 border-t border-white/5 flex items-baseline justify-between">
        <span className="text-[9px] uppercase tracking-[0.3em] text-[#757575] font-bold">PPS</span>
        <span className="font-heading text-lg text-white">{ppsText}</span>
      </div>
    </div>
  );
}

function RankBadge({ n }) {
  return (
    <span className="inline-flex items-center justify-center text-[9px] font-bold rounded-full w-5 h-5 bg-[var(--org-accent)] text-[#121212]" title={`Personal rank #${n}`}>
      {n}
    </span>
  );
}

function CategoryLabel({ iconKey, label }) {
  const Icon = CATEGORY_ICONS[iconKey];
  return (
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="w-9 h-9 flex items-center justify-center border border-[var(--org-accent)]/30 bg-[var(--org-accent)]/10 shrink-0">
          <Icon className="w-4 h-4 text-[var(--org-accent)]" />
        </div>
      )}
      <div className="font-heading text-[13px] uppercase tracking-[0.15em] text-white font-bold leading-tight">{label}</div>
    </div>
  );
}

function PdfCategoryIcon({ iconKey }) {
  const Icon = CATEGORY_ICONS[iconKey];
  if (!Icon) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, border: "1px solid #d8b854", background: "#fdf6df", flexShrink: 0 }}>
      <Icon style={{ width: 11, height: 11, color: "#8a7020" }} />
    </span>
  );
}

const COACH_ACTIONS = [
  { key: "more",    label: "MORE",    tip: "Take more of these shots",                     color: "#4ade80" },
  { key: "convert", label: "CONVERT", tip: "Take less — convert into something more efficient", color: "#f87171" },
  { key: "improve", label: "IMPROVE", tip: "Improve this — it's a big part of your game",  color: "#d8b854" },
];

function CoachActionPicker({ current, onPick, disabled, testId }) {
  return (
    <div className="inline-flex gap-1" data-testid={testId}>
      {COACH_ACTIONS.map(a => {
        const active = current === a.key;
        return (
          <button
            key={a.key}
            disabled={disabled}
            onClick={() => onPick(active ? null : a.key)}
            title={a.tip}
            className={`px-2 py-1 text-[9px] font-bold tracking-[0.1em] border transition-colors ${active ? "text-[#121212]" : "text-[#a0a0a0] hover:text-white"} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
            style={active ? { background: a.color, borderColor: a.color } : { borderColor: "rgba(255,255,255,0.15)" }}
            data-testid={`${testId}-${a.key}`}>
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

function Report({ analysis, playerName, printRef, canEdit, onUpdateAction, onToggleIncrease, profiles }) {
  const nar = analysis.narrative || {};
  const cats = analysis.analysis?.categories || [];
  const box = analysis.analysis?.box_score || null;
  const catByKey = Object.fromEntries(cats.map(c => [c.key, c]));
  const growth = nar.headline_growth || [];
  const strengths = nar.headline_strengths || [];
  const recs = nar.recommendations || {};
  const actions = analysis.coach_actions || {};
  return (
    <div className="space-y-6" data-testid="sd-report">
      {/* Synergy-style Box Score card */}
      {box && (box.fga2 > 0 || box.fga3 > 0 || box.fta > 0) && (
        <div className="border border-white/10 bg-[#141414] p-5" data-testid="sd-box-score">
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)] mb-3">Box Score</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BoxSlot label="2PT" made={box.fgm2} att={box.fga2} pct={box.fg2_pct} pps={box.pps2} />
            <BoxSlot label="3PT" made={box.fgm3} att={box.fga3} pct={box.fg3_pct} pps={box.pps3} />
            <BoxSlot label="FT"  made={box.ftm}  att={box.fta}  pct={box.ft_pct}  pps={box.pps_ft} />
          </div>
        </div>
      )}
      {/* Shot Profile graphic */}
      <div className="border border-white/10 bg-[#141414] p-5 md:p-8">
        <ShotProfileGraphic categories={cats} flags={analysis.increase_flags} canEdit={canEdit} onToggleIncrease={onToggleIncrease} />
      </div>
      {/* Headline */}
      {(growth.length > 0 || strengths.length > 0) && (
        <div className="border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/5 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-[var(--org-accent)]" />
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)]">Coach's Headline</div>
          </div>
          {growth.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#f87171] font-bold mb-2">Top Growth Areas</div>
              <ul className="space-y-2">
                {growth.map((g, i) => (
                  <li key={i} className="text-sm text-white leading-relaxed flex gap-2">
                    <ChevronRight className="w-3.5 h-3.5 text-[var(--org-accent)] shrink-0 mt-1" />
                    <span><span className="font-bold">{catByKey[g.category_key]?.label || g.category_key}:</span> {g.sentence}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {strengths.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#4ade80] font-bold mb-2">Strengths to Lean Into</div>
              <ul className="space-y-2">
                {strengths.map((s, i) => (
                  <li key={i} className="text-sm text-white leading-relaxed flex gap-2">
                    <ChevronRight className="w-3.5 h-3.5 text-[var(--org-accent)] shrink-0 mt-1" />
                    <span><span className="font-bold">{catByKey[s.category_key]?.label || s.category_key}:</span> {s.sentence}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Full breakdown table */}
      <div className="border border-white/10 bg-[#141414] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#101010]">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Category</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">FGM/FGA</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Eff%</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Usage%</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">PPS</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Avg PPS</th>
              <th className="text-center px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Rating</th>
              <th className="text-center px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Coach Action</th>
            </tr>
          </thead>
          <tbody>
            {cats.map(c => {
              const r = RATING_STYLE[c.rating] || RATING_STYLE.insufficient;
              const isFtRate = c.key === "ft_rate";
              const vs = c.vs_avg;
              const showDelta = vs !== null && vs !== undefined;
              const deltaColor = vs > 0.001 ? "#4ade80" : vs < -0.001 ? "#f87171" : "#a0a0a0";
              const rec = recs[c.key];
              return (
                <React.Fragment key={c.key}>
                  <tr className="border-t border-white/5">
                    <td className="px-4 pt-3 pb-1"><CategoryLabel iconKey={c.key} label={c.label} /></td>
                    <td className="px-4 pt-3 pb-1 text-right font-mono text-[#c0c0c0]">{isFtRate ? `${Math.round(c.fta || 0)} FTA` : `${Math.round(c.fgm || 0)}/${Math.round(c.fga || 0)}`}</td>
                    <td className="px-4 pt-3 pb-1 text-right text-white">{isFtRate ? pct(c.ft_rate) : pct(c.efficiency)}</td>
                    <td className="px-4 pt-3 pb-1 text-right text-[#c0c0c0]">
                      {isFtRate ? "—" : (
                        <span className="inline-flex items-center gap-1.5">
                          {c.usage_rank && <RankBadge n={c.usage_rank} />}
                          <span>{pct(c.usage)}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 pt-3 pb-1 text-right text-[#c0c0c0]">
                      {isFtRate ? "—" : (
                        <span className="inline-flex items-center gap-1.5">
                          {c.pps_rank && <RankBadge n={c.pps_rank} />}
                          <span>{num(c.pps)}{showDelta && <span className="ml-1 text-[10px] font-bold" style={{ color: deltaColor }}>({vs > 0 ? "+" : ""}{vs.toFixed(2)})</span>}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 pt-3 pb-1 text-right text-[#757575]">{isFtRate ? "—" : num(c.avg_pps)}</td>
                    <td className="px-4 pt-3 pb-1 text-center">
                      <span className="text-[9px] font-bold tracking-[0.2em] px-2 py-1" style={{ background: r.bg, color: r.text }}>{r.label}</span>
                    </td>
                    <td className="px-4 pt-3 pb-1 text-center">
                      <CoachActionPicker current={actions[c.key]} onPick={(v) => onUpdateAction(c.key, v)} disabled={!canEdit || isFtRate} testId={`sd-action-${c.key}`} />
                    </td>
                  </tr>
                  {rec && (
                    <tr className="border-b border-white/5">
                      <td colSpan={8} className="px-4 pt-1 pb-3">
                        <div className="text-[13px] text-[#d8d8d8] leading-relaxed pl-1 border-l-2 border-[var(--org-accent)]/40 pl-3">
                          <span className="text-[9px] uppercase tracking-[0.3em] text-[var(--org-accent)] font-bold mr-2">Recommendation</span>
                          {rec}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Offscreen printable pages (landscape letter: 11x8.5 → 1320x1020 px canvas) */}
      <div style={{ position: "fixed", left: "-10000px", top: 0, width: "1320px", pointerEvents: "none" }} aria-hidden="true">
        <PrintableReport refs={printRef} analysis={analysis} playerName={playerName} profiles={profiles} />
      </div>
    </div>
  );
}

const PDF_FONT = "Helvetica, Arial, sans-serif";
function PdfHeader({ playerName, analysis, subtitle, page }) {
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div style={{ borderBottom: "3px solid #d8b854", paddingBottom: 12, marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: 3, color: "#6b7280", textTransform: "uppercase", fontWeight: 700 }}>Shot Diet · {subtitle}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#111", marginTop: 4 }}>{playerName || "Player"}</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{analysis.label || "Analysis"} · {today}</div>
      </div>
      <div style={{ fontSize: 10, color: "#9ca3af", letterSpacing: 2 }}>PAGE {page}</div>
    </div>
  );
}

function PrintableReport({ refs, analysis, playerName, profiles }) {
  const tr = profiles?.transition;
  const pt = profiles?.play_type;
  const cats = analysis.analysis?.categories || [];
  const box = analysis.analysis?.box_score || null;
  const catByKey = Object.fromEntries(cats.map(c => [c.key, c]));
  const nar = analysis.narrative || {};
  const growth = nar.headline_growth || [];
  const strengths = nar.headline_strengths || [];
  const recs = nar.recommendations || {};
  const RATE_COLOR = { great: "#065f46", solid: "#8a7020", needs_work: "#b91c1c", insufficient: "#6b7280" };
  const RATE_LBL   = { great: "GREAT", solid: "SOLID", needs_work: "NEEDS WORK", insufficient: "TOO SMALL" };
  const boxCell = (mk, at, p) => {
    const pv = p === null || p === undefined ? "—" : `${(p * 100).toFixed(1)}%`;
    return `${Math.round(mk || 0)}/${Math.round(at || 0)}  ·  ${pv}`;
  };
  const hasProfileRecs = (p) => p?.narrative?.recommendations && Object.keys(p.narrative.recommendations).length > 0;
  return (
    <>
      {/* PAGE 1 — graphics */}
      <div ref={(el) => { refs.current.page1 = el; }} style={{ background: "#ffffff", padding: 32, fontFamily: PDF_FONT, width: 1320, boxSizing: "border-box" }} data-testid="sd-pdf-page1">
        <PdfHeader playerName={playerName} analysis={analysis} subtitle="Shot Profile" page={1} />
        {box && (box.fga2 > 0 || box.fga3 > 0 || box.fta > 0) && (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", padding: "10px 16px", marginBottom: 20, display: "flex", justifyContent: "space-around", gap: 16 }}>
            <div><div style={{ fontSize: 10, letterSpacing: 2, color: "#6b7280", fontWeight: 700 }}>2PT</div><div style={{ fontSize: 15, color: "#111", fontWeight: 700 }}>{boxCell(box.fgm2, box.fga2, box.fg2_pct)}</div><div style={{ fontSize: 11, color: "#6b7280" }}>PPS {box.pps2 !== null && box.pps2 !== undefined ? Number(box.pps2).toFixed(2) : "—"}</div></div>
            <div><div style={{ fontSize: 10, letterSpacing: 2, color: "#6b7280", fontWeight: 700 }}>3PT</div><div style={{ fontSize: 15, color: "#111", fontWeight: 700 }}>{boxCell(box.fgm3, box.fga3, box.fg3_pct)}</div><div style={{ fontSize: 11, color: "#6b7280" }}>PPS {box.pps3 !== null && box.pps3 !== undefined ? Number(box.pps3).toFixed(2) : "—"}</div></div>
            <div><div style={{ fontSize: 10, letterSpacing: 2, color: "#6b7280", fontWeight: 700 }}>FT</div><div style={{ fontSize: 15, color: "#111", fontWeight: 700 }}>{boxCell(box.ftm, box.fta, box.ft_pct)}</div><div style={{ fontSize: 11, color: "#6b7280" }}>PPS {box.pps_ft !== null && box.pps_ft !== undefined ? Number(box.pps_ft).toFixed(2) : "—"}</div></div>
          </div>
        )}
        <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }} data-testid="sd-pdf-profiles">
          <div style={{ flex: 1, minWidth: 0 }}>
            {tr ? <TransitionCompare rows={tr.rows || []} light testId="sd-pdf-transition" /> : <PdfEmpty label="Transition vs Half Court" />}
          </div>
          <div style={{ flex: 1.15, minWidth: 0 }}>
            <ShotProfileGraphic categories={cats} flags={analysis.increase_flags} light testId="sd-shot-profile-pdf" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {pt ? <ProfileBars rows={profileBars("play_type", pt.rows || [])} title="Play Types" light testId="sd-pdf-play-type" /> : <PdfEmpty label="Play Types" />}
          </div>
        </div>
        <div style={{ marginTop: 24, paddingTop: 10, borderTop: "1px solid #e5e7eb", fontSize: 10, color: "#9ca3af", textAlign: "center" }}>Maximize Analytics</div>
      </div>

      {/* PAGE 2 — recommendations */}
      <div ref={(el) => { refs.current.page2 = el; }} style={{ background: "#ffffff", padding: 32, fontFamily: PDF_FONT, width: 1320, boxSizing: "border-box" }} data-testid="sd-pdf-page2">
        <PdfHeader playerName={playerName} analysis={analysis} subtitle="Coach Recommendations" page={2} />
        <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
          <div style={{ flex: 1.5, minWidth: 0 }}>
            <PdfSectionTitle>Shot Diet</PdfSectionTitle>
            {growth.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#b91c1c", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Top Growth Areas</div>
                {growth.map((g, i) => <div key={i} style={{ fontSize: 12, color: "#111827", marginBottom: 4, lineHeight: 1.5 }}><strong>{catByKey[g.category_key]?.label || g.category_key}:</strong> {g.sentence}</div>)}
              </div>
            )}
            {strengths.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "#065f46", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Strengths to Lean Into</div>
                {strengths.map((s, i) => <div key={i} style={{ fontSize: 12, color: "#111827", marginBottom: 4, lineHeight: 1.5 }}><strong>{catByKey[s.category_key]?.label || s.category_key}:</strong> {s.sentence}</div>)}
              </div>
            )}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  {["Category", "Eff", "Usage", "PPS", "Avg", "Rating"].map((h, i) => (
                    <th key={h} style={{ padding: "6px 8px", fontSize: 9, textAlign: i === 0 ? "left" : i === 5 ? "center" : "right", color: "#4b5563", textTransform: "uppercase", letterSpacing: 2 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cats.map(c => {
                  const isFt = c.key === "ft_rate";
                  const vs = c.vs_avg;
                  const showDelta = vs !== null && vs !== undefined;
                  const deltaColor = vs > 0.001 ? "#15803d" : vs < -0.001 ? "#b91c1c" : "#6b7280";
                  const action = (analysis.coach_actions || {})[c.key];
                  const actionLabel = action === "more" ? "MORE" : action === "convert" ? "CONVERT" : action === "improve" ? "IMPROVE" : "";
                  const actionColor = action === "more" ? "#065f46" : action === "convert" ? "#b91c1c" : action === "improve" ? "#8a7020" : "#9ca3af";
                  const usageStr = c.usage_rank ? `#${c.usage_rank} · ${pct(c.usage)}` : pct(c.usage);
                  const ppsStr = c.pps_rank ? `#${c.pps_rank} · ${num(c.pps)}` : num(c.pps);
                  const rec = recs[c.key];
                  return (
                    <React.Fragment key={c.key}>
                      <tr style={{ borderTop: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "7px 8px 2px", fontSize: 11, color: "#111", fontWeight: 700 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <PdfCategoryIcon iconKey={c.key} />
                            <span style={{ textTransform: "uppercase", letterSpacing: 1 }}>{c.label}</span>
                            {actionLabel && <span style={{ display: "inline-block", marginLeft: 6, background: actionColor, color: "#fff", fontSize: 8, padding: "2px 5px", fontWeight: 700, letterSpacing: 1 }}>{actionLabel}</span>}
                          </div>
                        </td>
                        <td style={{ padding: "7px 8px 2px", fontSize: 11, textAlign: "right", color: "#374151" }}>{isFt ? pct(c.ft_rate) : pct(c.efficiency)}</td>
                        <td style={{ padding: "7px 8px 2px", fontSize: 11, textAlign: "right", color: "#374151" }}>{isFt ? "—" : usageStr}</td>
                        <td style={{ padding: "7px 8px 2px", fontSize: 11, textAlign: "right", color: "#111" }}>
                          {isFt ? "—" : <>{ppsStr}{showDelta && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: deltaColor }}>({vs > 0 ? "+" : ""}{vs.toFixed(2)})</span>}</>}
                        </td>
                        <td style={{ padding: "7px 8px 2px", fontSize: 11, textAlign: "right", color: "#6b7280" }}>{isFt ? "—" : num(c.avg_pps)}</td>
                        <td style={{ padding: "7px 8px 2px", textAlign: "center" }}>
                          <span style={{ background: RATE_COLOR[c.rating], color: "#fff", fontSize: 8, fontWeight: 700, padding: "3px 7px", letterSpacing: 1 }}>{RATE_LBL[c.rating]}</span>
                        </td>
                      </tr>
                      {rec && (
                        <tr>
                          <td colSpan={6} style={{ padding: "0 8px 8px" }}>
                            <div style={{ borderLeft: "3px solid #d8b854", padding: "1px 10px", fontSize: 11, color: "#374151", lineHeight: 1.5 }}>
                              <span style={{ fontSize: 8, letterSpacing: 2, color: "#8a7020", fontWeight: 700, textTransform: "uppercase", marginRight: 6 }}>Rec</span>{rec}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 22 }}>
            <div>
              <PdfSectionTitle>Transition vs Half Court</PdfSectionTitle>
              {hasProfileRecs(tr) ? <ProfileRecs narrative={tr.narrative} rows={tr.rows} light testId="sd-pdf-transition-recs" /> : <PdfNoRecs />}
            </div>
            <div>
              <PdfSectionTitle>Play Types</PdfSectionTitle>
              {hasProfileRecs(pt) ? <ProfileRecs narrative={pt.narrative} rows={pt.rows} light testId="sd-pdf-play-type-recs" /> : <PdfNoRecs />}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 24, paddingTop: 10, borderTop: "1px solid #e5e7eb", fontSize: 10, color: "#9ca3af", textAlign: "center" }}>
          Maximize Analytics · Shot selection analysis powered by Claude Sonnet 5.
        </div>
      </div>
    </>
  );
}

const PdfSectionTitle = ({ children }) => (
  <div style={{ fontSize: 13, fontWeight: 800, color: "#111", letterSpacing: 2, textTransform: "uppercase", borderBottom: "2px solid #d8b854", paddingBottom: 4, marginBottom: 10 }}>{children}</div>
);
const PdfEmpty = ({ label }) => (
  <div style={{ border: "1px dashed #d1d5db", padding: "40px 12px", textAlign: "center", color: "#9ca3af", fontSize: 11, letterSpacing: 1 }}>{label}: no data entered</div>
);
const PdfNoRecs = () => <div style={{ fontSize: 11, color: "#9ca3af" }}>No recommendations generated yet.</div>;
